import { Link } from "react-router";
import Seo from "../../components/Seo.tsx";
import LegalPage from "./LegalPage.tsx";

export default function Terms() {
  return (
    <>
    <Seo title="Terms of Service" description="Terms of Service for BeAdoré Candles — purchases, shipping, intellectual property, and more." path="/terms" noIndex />
    <LegalPage
      eyebrow="The Fine Print"
      title="Terms of Service"
      updatedAt="April 2026"
    >
      <p>
        Welcome to BeAdoré. These Terms of Service (the <em>"Terms"</em>) govern
        your access to and use of the website at{" "}
        <strong>beadorecandles.com</strong> (the <em>"Site"</em>) and any
        products, services, or features offered by BeAdoré (the{" "}
        <em>"Services"</em>). By visiting, registering, or placing an order with
        us, you agree to these Terms in full.
      </p>

      <h2>1 · Who we are</h2>
      <p>
        BeAdoré is an independent, hand-poured candle atelier based in the
        United States. When these Terms say <em>"we," "us,"</em> or{" "}
        <em>"our,"</em> we mean BeAdoré. When they say <em>"you"</em> or{" "}
        <em>"your,"</em> we mean you, the person visiting our Site or placing an
        order.
      </p>

      <h2>2 · The Services</h2>
      <p>We currently offer three things on the Site:</p>
      <ul>
        <li>
          <strong>The Collection</strong> — hand-poured candles, sculptural
          waxes, wax melts, and room sprays available for direct purchase.
        </li>
        <li>
          <strong>Compose a Candle</strong> — an experience where you enter
          your first name and birthday and receive an AI-composed personal
          candle reading (a scent story, a name, and a dedication). This
          feature is free, for personal enjoyment, and is not a prediction of
          the future or professional advice of any kind.
        </li>
        <li>
          <strong>BeAdoré Private Letter List</strong> — an opt-in email
          newsletter for occasional updates and new releases.
        </li>
      </ul>

      <h2>3 · Eligibility</h2>
      <p>
        You must be at least 18 years old, or the age of legal majority where
        you live, to place an order. By purchasing from us, you represent that
        you are able to enter into a binding contract. We do not knowingly
        collect information from children under 13.
      </p>

      <h2>4 · Orders, pricing, and payment</h2>
      <p>
        All prices on the Site are listed in United States Dollars (USD) unless
        otherwise noted. Taxes, shipping, and any applicable duties are added at
        checkout. Payment is processed securely by{" "}
        <strong>Stripe, Inc.</strong> — BeAdoré never sees or stores your full
        card number. By placing an order you authorise Stripe to charge your
        payment method for the total shown at checkout.
      </p>
      <p>
        We reserve the right to decline or cancel any order at our discretion
        — for example, if a product is mispriced, out of stock, or we suspect
        fraud. If we cancel a paid order, we will issue a full refund to your
        original payment method within ten (10) business days.
      </p>

      <h2>5 · Shipping</h2>
      <p>
        Orders are fulfilled from our studio and typically ship within three
        (3) to five (5) business days. Delivery times depend on your carrier.
        Risk of loss passes to you once the carrier takes possession. If your
        candle arrives damaged, please contact us within seven (7) days of
        delivery — see our{" "}
        <Link to="/refunds" className="underline">
          Refund Policy
        </Link>
        .
      </p>

      <h2>6 · Intellectual property</h2>
      <p>
        The Site, its content, typography, imagery, and the BeAdoré name and
        logo are the property of BeAdoré or used under licence, and are
        protected by copyright, trademark, and other laws. You may not copy,
        reproduce, or use our materials commercially without written permission.
        You may share photos of candles you've purchased on social media —
        we'd be honoured.
      </p>

      <h2>7 · The Horoscope Candle feature</h2>
      <p>
        The AI-composed candle reading is generated by a third-party large
        language model (currently Google Gemini). It is produced for aesthetic
        and personal enjoyment only. It is not astrology, fortune-telling,
        medical advice, psychological advice, or counsel of any kind. The first
        name and birth date you provide are used solely to derive your zodiac
        sign and compose the reading, and are stored alongside the composition
        so you can revisit it via the share link.
      </p>

      <h2>8 · Email list</h2>
      <p>
        When you compose a horoscope candle or place an order, we add your
        email address to the BeAdoré Private Letter List so we can send you
        occasional brand news and new releases. You can unsubscribe at any time
        using the link at the bottom of any email we send you. See our{" "}
        <Link to="/privacy" className="underline">
          Privacy Policy
        </Link>{" "}
        for details.
      </p>

      <h2>9 · Acceptable use</h2>
      <p>
        Please don't try to break the Site. Specifically, you agree not to: (a)
        reverse engineer, scrape, or abuse the Services; (b) impersonate another
        person; (c) use the Site for unlawful purposes; (d) attempt to overwhelm
        our servers or interfere with other users.
      </p>

      <h2>10 · Disclaimers</h2>
      <p>
        The Site and the Services are provided <em>"as is"</em> and{" "}
        <em>"as available."</em> To the maximum extent permitted by law, BeAdoré
        disclaims all warranties, express or implied, including merchantability,
        fitness for a particular purpose, and non-infringement. We do not
        guarantee that the Site will be uninterrupted, error-free, or
        uninterruptedly available.
      </p>

      <h2>11 · Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, BeAdoré's total liability for
        any claim arising out of or relating to these Terms or the Services is
        limited to the amount you paid for the specific order giving rise to
        the claim. BeAdoré is not liable for indirect, incidental, special, or
        consequential damages.
      </p>

      <h2>12 · Governing law</h2>
      <p>
        These Terms are governed by the laws of the United States and the
        state in which BeAdoré is registered, without regard to conflict-of-law
        principles. Any dispute arising under these Terms will be brought
        exclusively in the courts of that state.
      </p>

      <h2>13 · Changes</h2>
      <p>
        We may update these Terms from time to time. When we do, we will revise
        the "Last updated" date at the top of this page. If changes are
        material, we will also notify you via the email address on file. Your
        continued use of the Site after changes take effect constitutes
        acceptance.
      </p>

      <h2>14 · Contact</h2>
      <p>
        Questions about these Terms? Write to us at{" "}
        <a href="mailto:hello@beadorecandles.com" className="underline">
          hello@beadorecandles.com
        </a>
        .
      </p>
    </LegalPage>
    </>
  );
}
